import { Button } from "@/components/ui/button";
import { TodoSourceFilter, todoSourceFilterOptions } from "@/lib/api/customer";
import { cn } from "@/lib/utils";

interface TodoSourceFilterButtonsProps {
  value: TodoSourceFilter;
  onValueChange: (value: TodoSourceFilter) => void;
  className?: string;
}

/**
 * Human / Both / Automation, as one segmented control.
 *
 * Three `Button`s rather than a `ToggleGroup`: there is no toggle-group
 * component in `@/components/ui` and adding a package is not ours to do. The
 * segmented look comes from the wrapper owning the border and the radius while
 * each button drops its own (`rounded-none`, no ring offset), so the three read
 * as one control instead of three adjacent controls.
 *
 * The selected segment is a filled `default` button and the other two are
 * `ghost`, which is the only state signal here — `aria-pressed` carries the same
 * fact for a screen reader, since colour alone would not.
 */
export function TodoSourceFilterButtons({ value, onValueChange, className }: TodoSourceFilterButtonsProps) {
  return (
    <div
      role="group"
      aria-label="Filter to-dos by who created them"
      className={cn("inline-flex items-center rounded-md border border-input overflow-hidden", className)}
    >
      {todoSourceFilterOptions.map((option: { value: TodoSourceFilter; label: string }, index: number) => (
        <Button
          key={option.value}
          type="button"
          size="sm"
          variant={value === option.value ? "default" : "ghost"}
          aria-pressed={value === option.value}
          data-testid={`todo-source-filter-${option.value}`}
          data-selected={value === option.value}
          onClick={() => onValueChange(option.value)}
          className={cn(
            "rounded-none focus-visible:ring-offset-0",
            index > 0 && "border-l border-input",
          )}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
